import { useMemo, type ReactNode } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Settings } from '@mint-lib/icons';
import {
  Chip,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';

import type {
  CellFormatType,
  ChannelCatalogItem,
  PlatformsDataContainer,
} from '../api/types.js';
import { Icon } from '../components/atoms/Icon.jsx';
import { FormattedNumberValue } from '../components/index.js';
import { useFormattedDate } from './index.js';

type CellRendererArgs = {
  formatType: CellFormatType;
  platformsByName: PlatformsDataContainer['platformsByName'];
  channelsByName: Record<string, ChannelCatalogItem>;
  mediaRowNamesById?: Record<string, string>;
  error: boolean;
};

const renderedTrancated = (value: string) => (
  <span className="block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
    {value}
  </span>
);

const renderedErrorValue = (value: string) => (
  <TruncateText>
    <span className="block min-w-0">{value}</span>
  </TruncateText>
);

const renderChip = (logoWithIcon: ReactNode, value: ReactNode) => (
  <Chip
    variant="outline"
    size="medium"
    color="secondary"
    className="max-w-full"
  >
    {logoWithIcon}
    {value}
  </Chip>
);

export const useCellValueRenderer = ({
  formatType,
  platformsByName,
  channelsByName,
  mediaRowNamesById,
  error,
}: CellRendererArgs) => {
  const { t } = useTranslation('@myn/import-hub');
  const { getFormattedDate } = useFormattedDate();

  const renderValue = error ? renderedErrorValue : renderedTrancated;

  const cellValueRenderersByType = useMemo(
    () =>
      ({
        id: (v: string) => renderValue(v),
        date: (v: string) =>
          renderValue(error ? v : (getFormattedDate(v) ?? v)),
        int: (v: string) => {
          const value = Number(v);

          if (Number.isNaN(value) || error) {
            return renderValue(v);
          }

          return (
            <FormattedNumberValue value={value} renderValue={renderValue} />
          );
        },
        float: (v: string) => renderValue(v),
        platform: (v: string) => {
          if (!v) {
            return null;
          }

          const platform = platformsByName[v];

          if (!platform) {
            return renderValue(v);
          }

          return renderChip(
            platform.logo ? (
              <img
                src={platform.logo}
                width={16}
                alt="platform logo"
                className="h-4 w-4"
              />
            ) : (
              <Settings className="fill-icon-primary" />
            ),
            renderValue(platform.label),
          );
        },
        channel: (v: string) => {
          if (!v) {
            return (
              <span
                className={typographyVariants({
                  variant: 'bodyShort01',
                  color: 'placeholder',
                })}
              >
                {t('Select a channel')}
              </span>
            );
          }

          const channel = channelsByName[v];

          if (!channel) {
            return renderValue(v);
          }

          return renderChip(
            channel.logo ? (
              <Icon component={channel.logo} />
            ) : (
              <Settings className="fill-icon-primary" />
            ),
            renderValue(channel.label),
          );
        },
        media_row: (v: string) => {
          if (!v) {
            return (
              <span
                className={typographyVariants({
                  variant: 'bodyShort01',
                  color: 'placeholder',
                })}
              >
                {t('Select a media row')}
              </span>
            );
          }

          const mediaRowName = mediaRowNamesById?.[v];

          if (mediaRowName) {
            return renderValue(mediaRowName);
          }

          return renderValue(v);
        },
      }) as const,
    [
      renderValue,
      getFormattedDate,
      error,
      platformsByName,
      channelsByName,
      t,
      mediaRowNamesById,
    ],
  );

  return cellValueRenderersByType[formatType];
};
